// @ts-expect-error TS7016, tinymce has not yet been migrated.
import tinymce from 'tinymce';
import { ICON_FONT_FAMILY } from 'HubStyleTokens/misc';
import * as Colors from 'HubStyleTokens/colors';
import { PropertyCategories } from 'rich-text-lib/constants/properties';
import { parseHtml, replaceNodes, addPostProcessTransform, addPreProcessTransform } from '../utils/transformer';
import { getHublVariable } from '../utils/hubl';
import { getPathsMap } from 'rich-text-lib/utils/buildProperties';
import { trackInteraction } from '../utils/usageTracking';
import { PERSONALIZATION_TOKEN_CASING } from '../constants/usageTracking';
import { getWebFontFamily } from '../utils/token';
import { HUBL_PATH, HUBL_PERSONALIZATION_PATTERN, FILTER_PATTERN_FRAGMENT, hsDataTokenConfig } from '../constants/hsdatatokens';
import { HS_PLUGINS } from '../constants/hsSettings';
import { HS_DATA_TOKEN } from '../constants/plugins';
import { RSS_CATEGORY } from '../constants/hsrsstokens';
export const createTokenString = ({
  path = '',
  defaultValue,
  filter
}) => {
  if (defaultValue === null) {
    return `{{ ${path}${filter ? `|${filter}` : ''} }}`;
  }
  return `{{ personalization_token('${path}', '${defaultValue}')${filter ? `|${filter}` : ''} }}`;
};
const getHasFilterSupport = dataTokenConfig => {
  const propertyFiltersCallback = dataTokenConfig[hsDataTokenConfig.PROPERTY_FILTERS_CALLBACK];
  return !!propertyFiltersCallback;
};
export const getTokenPattern = dataTokenConfig => {
  if (!dataTokenConfig) return HUBL_PATH;
  const hasFilterSupport = getHasFilterSupport(dataTokenConfig);
  if (!hasFilterSupport) return HUBL_PATH;
  return new RegExp(`{{-?\\s*([a-zA-Z0-9_()\\-[\\]]+(?:[.][a-zA-Z0-9_()\\-[\\]]+)*)\\s*${hasFilterSupport ? FILTER_PATTERN_FRAGMENT : ''}\\s*-?}}`, 'g');
};
export const getTokenPatternWithDefault = dataTokenConfig => {
  if (!dataTokenConfig) return HUBL_PERSONALIZATION_PATTERN;
  const hasFilterSupport = getHasFilterSupport(dataTokenConfig);
  if (!hasFilterSupport) return HUBL_PERSONALIZATION_PATTERN;
  return new RegExp(`{{-?\\s*personalization_token\\(\\s*["']([a-zA-Z0-9_()\\-[\\]]+(?:[.][a-zA-Z0-9_()\\-[\\]]+)*)["']\\s*,\\s*["'](.*?)["']{1}\\s*\\)${hasFilterSupport ? FILTER_PATTERN_FRAGMENT : ''}\\s*-?}}`, 'g');
};
export const getDefaultValueType = defaultValue => {
  if (defaultValue === '') {
    return 'empty';
  } else if (defaultValue === null) {
    return 'global';
  } else {
    return 'local';
  }
};
export function getHsDataTokenSettings(editor) {
  const pluginSettings = editor.settings ? editor.settings[HS_PLUGINS] : null;
  if (!pluginSettings || !pluginSettings[HS_DATA_TOKEN]) {
    return null;
  }
  return pluginSettings[HS_DATA_TOKEN];
}

/**
 *
 * @param {String} category token category
 * @returns {String} icon code for custom font
 */
const getTokenIconFromCategory = (category = '') => {
  switch (category.toLowerCase()) {
    case PropertyCategories.CONTACT:
      return 'contacts';
    case PropertyCategories.COMPANY:
      return 'companies';
    case PropertyCategories.DEAL:
      return 'deals';
    case PropertyCategories.QUOTE:
    case PropertyCategories.LINE_ITEM:
      return 'salesQuote';
    case PropertyCategories.TICKET:
      return 'ticket';
    case PropertyCategories.INVOICE:
      return 'invoice';
    case PropertyCategories.CART:
      return 'cart';
    default:
      return 'hubDB';
  }
};

/**
 *
 * @param {String} hublVar token hubl string
 * @returns {Object} token propertites
 */

export const splitHublVar = hublVar => {
  const parts = hublVar.split('.');
  if (!parts.length) {
    return null;
  }
  if (parts.length === 1) {
    return {
      property: parts[0]
    };
  }
  return {
    category: parts[0],
    property: parts[1]
  };
};

/**
 *
 * @param {String} str maybe token hubl string
 * @returns {(Object|null)} token propertites
 */
export const getTokenProperties = str => {
  const hublVar = getHublVariable(str);
  if (!hublVar) {
    return null;
  }
  return splitHublVar(hublVar);
};

/**
 *
 * @param {Object} properties
 * @returns {String} Translated property label
 */
export const getTokenLabel = (dataTokenPath, dataTokenConfig) => {
  const pathsMap = getPathsMap(dataTokenConfig) || {};
  const pathData = pathsMap[dataTokenPath];
  return pathData ? pathData.label : '';
};
export const isEditableToken = (editor, node) => {
  return editor.dom.is(node, 'img[data-mce-hubspot-object=true][data-mce-placeholder=true][data-mce-property-category!=post]');
};
export function tokenIsOnlyChild(editor, node) {
  if (isEditableToken(editor, node)) return true;
  const childNodes = [...node.childNodes];
  if (childNodes.length === 1) {
    return tokenIsOnlyChild(editor, childNodes[0]);
  }
  return false;
}

// Recursively truncate the token label then add a trailing ellipsis
const truncateTokenLabelWithEllipsis = (context, maxLabelWidth, label) => {
  const truncatedLabel = `${label}...`;
  const labelWidth = context.measureText(truncatedLabel).width;
  if (labelWidth <= maxLabelWidth) {
    return {
      truncatedLabel,
      labelWidth
    };
  }
  return truncateTokenLabelWithEllipsis(context, maxLabelWidth, label.slice(0, label.length - 1).trim());
};

// We need this object for testing purposes
export const TruncateTokenLabelExport = {
  truncateTokenLabelWithEllipsis
};
const UPSCALING_FACTOR = 4;
const align = value => Math.floor(value / UPSCALING_FACTOR) * UPSCALING_FACTOR;
export const renderTokenBase = (label, category, setImgAttrFromProps, maxTokenWidth, hasDataSensitivity) => {
  const tokenHeight = 26 * UPSCALING_FACTOR;
  const textBaselineBuffer = 7 * UPSCALING_FACTOR;
  const tokenWidthBuffer = 16 * UPSCALING_FACTOR;
  const spaceAfterIcon = 8 * UPSCALING_FACTOR;
  const textSize = 16 * UPSCALING_FACTOR;
  const font = `${textSize}px ${getWebFontFamily()}`;
  const iconFont = `${textSize}px ${ICON_FONT_FAMILY}`;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }
  const tokenColors = {
    color: Colors.PANTERA,
    backgroundColor: Colors.OLAF
  };
  const tokenIcon = getTokenIconFromCategory(category);
  try {
    context.font = iconFont;
  } catch (error) {
    console.error('Could not set font on context', error);
  }
  const iconWidth = context.measureText(tokenIcon).width;

  // space breakdown of token image along width
  // | 1/2 token width buffer | icon width | 8px buffer | label width | 1/2 token width buffer |
  const iconWithBufferWidth = iconWidth + spaceAfterIcon + tokenWidthBuffer;

  // This is a temp fix because of a bug in FF that cannot set
  // font on a canvas 2d context within an iframe that is hidden
  // see: https://issues.hubspotcentral.com/browse/COSEMAIL-10478
  try {
    context.font = font;
  } catch (error) {
    console.error('Could not set font on context', error);
  }
  let labelWidth = context.measureText(label).width;
  let truncatedLabel = label;
  if (typeof maxTokenWidth === 'number') {
    const maxLabelWidth = maxTokenWidth * UPSCALING_FACTOR - iconWithBufferWidth;
    if (labelWidth > maxLabelWidth) {
      ({
        truncatedLabel,
        labelWidth
      } = TruncateTokenLabelExport.truncateTokenLabelWithEllipsis(context, maxLabelWidth, label));
    }
  }
  const totalWidth = iconWithBufferWidth + labelWidth;
  canvas.setAttribute('width', `${align(totalWidth)}px`);
  canvas.setAttribute('height', `${align(tokenHeight)}px`);
  context.fillStyle = tokenColors.color;
  context.textAlign = 'left';
  try {
    context.font = iconFont;
  } catch (error) {
    console.error('Could not set font on context', error);
  }
  let x = tokenWidthBuffer / 2;
  let y = tokenHeight - textBaselineBuffer;
  context.fillText(tokenIcon, align(x), align(y));

  // This is a temp fix because of a bug in FF that cannot set
  // font on a canvas 2d context within an iframe that is hidden
  // see: https://issues.hubspotcentral.com/browse/COSEMAIL-10478
  try {
    context.font = font;
  } catch (error) {
    console.error('Could not set font on context', error);
  }
  x = totalWidth - labelWidth - tokenWidthBuffer / 2;
  y = tokenHeight - textBaselineBuffer;
  context.fillText(truncatedLabel, align(x), align(y));
  const image = document.createElement('img');
  image.setAttribute('data-mce-placeholder', 'true');
  image.setAttribute('data-mce-resize', 'false');
  image.setAttribute('data-mce-truncated-label', String(truncatedLabel !== label));
  image.classList.add('mce-token');
  image.setAttribute('style', `width: ${Math.floor(parseInt(canvas.getAttribute('width') || '', 10) / UPSCALING_FACTOR + 2)}px !important; height: ${Math.floor(parseInt(canvas.getAttribute('height') || '', 10) / UPSCALING_FACTOR + 2)}px !important; background: ${hasDataSensitivity ? Colors.CANDY_APPLE_LIGHT : tokenColors.backgroundColor}; border: 1px solid ${hasDataSensitivity ? Colors.CANDY_APPLE_DARK : Colors.FLINT}; border-radius: 2px; vertical-align: middle; box-sizing: border-box;`);
  image.setAttribute('src', canvas.toDataURL());
  setImgAttrFromProps(image);
  return image;
};

// Check for an improperly cased (lowercased) `category` compared
// to the appropriate case-sensitive FQN, then track this in amplitude.
// Part of https://git.hubteam.com/HubSpot/Critsit/issues/2576 postmortem.
const checkForImproperlyCasedFQNs = (editor, supportedCrmObjectFQNs, category) => {
  const properlyCasedFQN = supportedCrmObjectFQNs.find(fqn => category === fqn.toLowerCase() && category !== fqn);
  if (properlyCasedFQN) {
    trackInteraction(editor, 'tinymcePersonalize', PERSONALIZATION_TOKEN_CASING, {
      category,
      fqn: properlyCasedFQN
    });
  }
  return !!properlyCasedFQN;
};
const replaceHubLWithToken = (editor, supportedCrmObjectFQNs, textContent, tokenPatternArray, renderFunction) => {
  let newTextContent = textContent;
  tokenPatternArray.forEach(({
    TOKEN_PATTERN,
    USES_DEFAULT
  }) => {
    if (USES_DEFAULT) {
      newTextContent = newTextContent.replace(TOKEN_PATTERN, (fullMatch, path, defaultValue, filter) => {
        if (path.startsWith(RSS_CATEGORY)) return fullMatch;
        const category = path.split('.')[0];
        const isImproperlyCasedFQN = checkForImproperlyCasedFQNs(editor, supportedCrmObjectFQNs, category);
        if (isImproperlyCasedFQN) return fullMatch;
        return renderFunction(Object.assign({
          path,
          category,
          defaultValue
        }, typeof filter === 'string' && {
          filter
        })) || fullMatch;
      });
    } else {
      newTextContent = newTextContent.replace(TOKEN_PATTERN, (fullMatch, path, filter) => {
        if (path.startsWith(RSS_CATEGORY)) return fullMatch;
        const category = path.split('.')[0];
        const isImproperlyCasedFQN = checkForImproperlyCasedFQNs(editor, supportedCrmObjectFQNs, category);
        if (isImproperlyCasedFQN) return fullMatch;
        return renderFunction(Object.assign({
          path,
          category
        }, typeof filter === 'string' && {
          filter
        })) || fullMatch;
      });
    }
  });
  return newTextContent;
};
const encode = tinymce.html.Entities.getEncodeFunc('named');
export const addTokenTransforms = (editor, supportedCrmObjectFQNs, tokenPatternArray, renderPlaceHolder, replacePlaceholderWithHubl) => {
  const replaceHubLWithPlaceholder = tokenNode => {
    const newTextContent = replaceHubLWithToken(editor, supportedCrmObjectFQNs, encode(tokenNode.textContent), tokenPatternArray, renderPlaceHolder);
    const doc = parseHtml(`<div>${newTextContent}</div>`);
    const firstChild = doc.body.firstChild;
    return firstChild && firstChild.childNodes;
  };
  addPreProcessTransform(editor, dom => {
    const textNodeIterator = dom.createNodeIterator(dom.body, NodeFilter.SHOW_TEXT, node => {
      tokenPatternArray.forEach(({
        TOKEN_PATTERN
      }) => {
        TOKEN_PATTERN.lastIndex = 0;
      });
      const nodeTextContent = node.textContent || '';
      if (tokenPatternArray.some(({
        TOKEN_PATTERN
      }) => {
        return TOKEN_PATTERN.test(nodeTextContent);
      })) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_REJECT;
    });
    const textNodes = [];
    let currentNode = textNodeIterator.nextNode();
    while (currentNode) {
      textNodes.push(currentNode);
      currentNode = textNodeIterator.nextNode();
    }
    replaceNodes(textNodes, replaceHubLWithPlaceholder);
  });
  addPostProcessTransform(editor, dom => {
    const tokenPlaceholderNodes = dom.querySelectorAll('[data-mce-token-path]:not([data-mce-rss-link]),[data-mce-property-name]:not([data-mce-rss-link])');
    replaceNodes(tokenPlaceholderNodes, replacePlaceholderWithHubl);
  });
};